import React from "react";
import {
  Box,
  Stack,
  Text,
  TextInput,
  Title,
  UnstyledButton,
} from "@mantine/core";
import { IconChevronRight, IconPlus, IconSearch } from "@tabler/icons-react";
import NewBadge from "../../common/NewBadge";
import Pill from "../../common/Pill";
import { SalonReport } from "../../../types/reports";
import {
  ReportCatalog,
  reportDisplayDescription,
  reportDisplayName,
} from "../shell/reportCatalog";
import { reportIcon } from "../shell/reportIcons";
import { useReportSections } from "../shell/useReportSections";
import styles from "./MobileReportsList.module.css";

export interface MobileReportsListProps {
  reports: SalonReport[];
  catalog: ReportCatalog;
  /** NEW reports in the Add-a-report picker ("N new" pill; newEntryCount). */
  newCount: number;
  onSelect: (report: SalonReport) => void;
  onAddReport: () => void;
}

interface RowProps {
  report: SalonReport;
  catalog: ReportCatalog;
  onSelect: (report: SalonReport) => void;
}

function ReportRow({ report, catalog, onSelect }: RowProps) {
  const entry = catalog.byType.get(report.report_type);
  const Icon = reportIcon(entry?.icon);
  const description = reportDisplayDescription(report, catalog);
  return (
    <UnstyledButton
      type="button"
      className={styles.row}
      onClick={() => onSelect(report)}
    >
      <Box component="span" className={styles.rowIcon} aria-hidden>
        <Icon size={17} />
      </Box>
      <Box component="span" className={styles.rowText}>
        <Box component="span" className={styles.rowTitleLine}>
          <Text component="span" className={styles.rowName}>
            {reportDisplayName(report, catalog)}
          </Text>
          {entry?.engine === "v2" && entry.isNew && <NewBadge />}
        </Box>
        {description && (
          <Text component="span" className={styles.rowDescription}>
            {description}
          </Text>
        )}
      </Box>
      <IconChevronRight size={17} className={styles.chevron} aria-hidden />
    </UnstyledButton>
  );
}

/**
 * The phone's Reports & Summaries screen (the desktop sidebar, full
 * screen): Add report with its "N new" pill, search for long lists, and one
 * card of reports per catalog category, Legacy last. Tapping a report opens
 * it. (No unsaved dot: on a phone a report's draft is resolved by the
 * navigation guard before the list can show.)
 */
export default function MobileReportsList({
  reports,
  catalog,
  newCount,
  onSelect,
  onAddReport,
}: MobileReportsListProps) {
  const { query, setQuery, showSearch, sections } = useReportSections(
    reports,
    catalog,
  );

  return (
    <Box className={styles.screen}>
      <Box className={styles.header}>
        <Title order={1} className={styles.title}>
          Reports &amp; Summaries
        </Title>
      </Box>
      <Stack gap="sm" className={styles.body}>
        <UnstyledButton
          type="button"
          className={styles.addButton}
          onClick={onAddReport}
          aria-label={
            newCount > 0 ? `Add report (${newCount} new)` : "Add report"
          }
        >
          <IconPlus size={16} aria-hidden />
          Add report
          {newCount > 0 && <Pill aria-hidden>{newCount} new</Pill>}
        </UnstyledButton>
        {showSearch && (
          <TextInput
            value={query}
            onChange={(e) => setQuery(e.currentTarget.value)}
            placeholder="Search your reports"
            aria-label="Search your reports"
            leftSection={<IconSearch size={15} aria-hidden />}
          />
        )}
        {sections.map(({ category, reports: sectionReports }) => (
          <Box key={category.key} role="group" aria-label={category.label}>
            <Text className={styles.sectionLabel}>{category.label}</Text>
            <Box className={styles.card}>
              {sectionReports.map((report) => (
                <ReportRow
                  key={report.id}
                  report={report}
                  catalog={catalog}
                  onSelect={onSelect}
                />
              ))}
            </Box>
          </Box>
        ))}
        {reports.length === 0 && (
          <Box className={styles.empty}>
            <Text className={styles.emptyTitle}>No reports in your list</Text>
            <Text className={styles.emptyText}>
              Add the reports you use most. You can add the same report more
              than once to keep different views.
            </Text>
          </Box>
        )}
        {reports.length > 0 && sections.length === 0 && (
          <Text className={styles.emptyText}>No reports match</Text>
        )}
      </Stack>
    </Box>
  );
}
